export type RequiredValueRef = {
  id: number
  code: string
  labelPl: string
}

export type GateMissingStatus = {
  trackId: number
  trackCode: string | null
  trackLabelPl: string | null
  requiredValues: RequiredValueRef[]
  currentValueId: number | null
  currentValueCode: string | null
  currentValueLabelPl: string | null
}

export type GateMissingDocument = {
  documentTypeId: number
  documentTypeCode: string | null
  documentTypeLabelPl: string | null
}

export type GateMissingField = {
  fieldCode: string
}

export type GateMissingFieldVerification = {
  fieldDefinitionId: number
  fieldCode: string
  fieldLabelPl: string | null
}

export type FinancingMetric =
  | 'PAID_PERCENT' | 'COVERAGE_PERCENT' | 'SCHEDULE_SET' | 'ALL_CREDITS_DISBURSED' | 'TOTALS_MATCH'
  | 'FINANCING_CASH_ONLY' | 'FINANCING_CREDIT_ONLY' | 'FINANCING_MIXED'

export const FINANCING_METRIC_LABEL: Record<FinancingMetric, string> = {
  PAID_PERCENT: '% wpłacone',
  COVERAGE_PERCENT: '% pokrycia',
  SCHEDULE_SET: 'Harmonogram ustalony',
  ALL_CREDITS_DISBURSED: 'Kredyty uruchomione',
  TOTALS_MATCH: 'Kwota = suma produktów',
  FINANCING_CASH_ONLY: 'Umowa gotówkowa',
  FINANCING_CREDIT_ONLY: 'Umowa kredytowa',
  FINANCING_MIXED: 'Kredyt + gotówka',
}

export const FINANCING_METRIC_NUMERIC: Record<FinancingMetric, boolean> = {
  PAID_PERCENT: true,
  COVERAGE_PERCENT: true,
  SCHEDULE_SET: false,
  ALL_CREDITS_DISBURSED: false,
  TOTALS_MATCH: false,
  FINANCING_CASH_ONLY: false,
  FINANCING_CREDIT_ONLY: false,
  FINANCING_MIXED: false,
}

export type GateMissingFinancing = {
  metric: FinancingMetric
  operator: string | null
  requiredValue: number | null
  currentValue: number | null
}

export type GateGroup = {
  groupNo: number
  satisfied: boolean
  missingStatus: GateMissingStatus[]
  missingDocuments: GateMissingDocument[]
  missingFields: GateMissingField[]
  missingFieldVerifications: GateMissingFieldVerification[]
  missingFinancing: GateMissingFinancing[]
}

export type Gate = {
  currentStageCode: string
  canComplete: boolean
  missing: GateMissingStatus[]            // flat - missing status of best unsatisfied group
  missingDocuments: GateMissingDocument[] // flat - missing documents of best unsatisfied group
  missingFields: GateMissingField[]
  missingFieldVerifications: GateMissingFieldVerification[]
  missingFinancing: GateMissingFinancing[]
  groups: GateGroup[]
}

export type RequirementKind = 'STATUS' | 'DOCUMENT' | 'FIELD' | 'FIELD_VERIFIED' | 'FINANCING'

export type StageRequirement = {
  id: number
  objectType: 'contract' | 'annex' | 'element'
  stageCode: string | null
  transition: string | null
  kind: RequirementKind
  // STATUS-only
  trackId: number | null
  trackCode: string | null
  trackLabelPl: string | null
  requiredValueId: number | null
  requiredValueCode: string | null
  requiredValueLabelPl: string | null
  // DOCUMENT-only
  documentTypeId: number | null
  documentTypeCode: string | null
  documentTypeLabelPl: string | null
  // FIELD-only
  fieldCode: string | null
  fieldLabelPl: string | null
  // FINANCING-only
  financingMetric: FinancingMetric | null
  financingOperator: string | null
  financingValue: number | null
  groupNo: number
  sortOrder: number
}

export type StageRequirementRequest = {
  objectType?: 'contract' | 'annex' | 'element'
  stageCode?: string
  transition?: string
  kind: RequirementKind
  trackId?: number | null
  requiredValueId?: number | null
  documentTypeId?: number | null
  fieldCode?: string | null
  financingMetric?: FinancingMetric | null
  financingValue?: number | null
  groupNo?: number
  sortOrder: number
}
